import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Reveal from "@/components/Reveal";
import EventTrailer from "@/components/EventTrailer";
import { TICKETS_URL, VENUE_URL, VENUE_NAME } from "@/lib/tickets";
import { MEDIA_FOLDER_URL } from "@/lib/press";

/**
 * /press-release
 *
 * Editorial, shareable press release page for Nick Fraser: Chapter II.
 * Doubles as a press-friendly URL (clean OG, clean copy) and links to a
 * typeset PDF generated server-side at /api/press/release.pdf.
 */

const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/press/release.pdf`;
const SHARE_URL =
  typeof window !== "undefined" ? window.location.href : "";

export default function PressRelease() {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL || PDF_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* no-op */
    }
  };

  return (
    <Layout>
      <main
        data-testid="press-release-page"
        className="bg-[color:var(--bone)] text-[color:var(--ink)]"
      >
        {/* ── Masthead ─────────────────────────────────────────────── */}
        <section
          className="bone-section pt-40 md:pt-56 pb-16 md:pb-24 border-b border-[color:var(--rule)]"
          data-testid="pr-masthead"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex flex-wrap items-center gap-4 mb-10">
                <span
                  className="hairline border border-[color:var(--ink)] px-3 py-1.5"
                  data-testid="pr-release-status"
                >
                  For Immediate Release
                </span>
                <span className="hairline opacity-55">
                  The Nick Fraser Estate
                </span>
              </div>
              <p className="hairline opacity-60 mb-6">Press Release</p>
              <h1
                className="font-serif italic text-5xl md:text-7xl leading-[0.98] tracking-tight"
                data-testid="pr-title"
              >
                Nick Fraser: Chapter II
              </h1>
              <p className="font-serif italic text-2xl md:text-3xl mt-6 opacity-85">
                31 July 2026  ·  Shoreditch Arts Club, London
              </p>
              <p className="font-serif italic text-xl md:text-2xl mt-10 opacity-75 max-w-3xl leading-[1.35]">
                A life rediscovered. A legacy returned to the East End.
              </p>

              {/* Actions */}
              <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-4">
                <a
                  href={PDF_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="pr-download-pdf"
                  className="hairline border border-[color:var(--ink)] px-6 py-3 hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500"
                >
                  Download PDF
                </a>
                <a
                  href={MEDIA_FOLDER_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="pr-media-folder"
                  className="hairline border border-[color:var(--ink)] px-6 py-3 hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500"
                >
                  Press image library →
                </a>
                <Link
                  to="/media-kit"
                  data-testid="pr-media-kit-link"
                  className="hairline opacity-80 hover:opacity-100 transition-opacity"
                >
                  Open media fact sheet →
                </Link>
                <button
                  type="button"
                  onClick={copy}
                  data-testid="pr-copy-link"
                  className="hairline opacity-80 hover:opacity-100 transition-opacity"
                >
                  {copied ? "Link copied ✓" : "Copy share link"}
                </button>
                <a
                  href="mailto:info@nickfraserartist.com?subject=Press%20enquiry%20%E2%80%94%20Nick%20Fraser%3A%20Chapter%20II"
                  className="hairline opacity-80 hover:opacity-100 transition-opacity"
                  data-testid="pr-email-press"
                >
                  Press contact
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── Body ────────────────────────────────────────────────── */}
        <section
          className="bone-section py-20 md:py-32"
          data-testid="pr-body"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
            {/* Sidebar — at-a-glance */}
            <Reveal className="md:col-span-4 md:sticky md:top-32 self-start">
              <div className="border-t border-[color:var(--ink)] pt-6">
                <p className="hairline opacity-60 mb-6">At a glance</p>
                <dl className="space-y-5">
                  <div>
                    <dt className="hairline opacity-55">Exhibition</dt>
                    <dd className="font-serif italic text-lg mt-1">
                      Nick Fraser: Chapter II
                    </dd>
                  </div>
                  <div>
                    <dt className="hairline opacity-55">Date</dt>
                    <dd className="font-serif italic text-lg mt-1">
                      Friday 31 July 2026
                    </dd>
                  </div>
                  <div>
                    <dt className="hairline opacity-55">Location</dt>
                    <dd className="font-serif italic text-lg mt-1 leading-snug">
                      <a
                        href={VENUE_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="link-quiet"
                        data-testid="pr-sidebar-venue-link"
                      >
                        Shoreditch Arts Club
                      </a>
                      <br />
                      6 Redchurch Street, London E2 7DD
                    </dd>
                  </div>
                  <div>
                    <dt className="hairline opacity-55">Admission</dt>
                    <dd className="font-serif italic text-lg mt-1">
                      £10 advance · £15 on the door
                    </dd>
                  </div>
                  <div>
                    <dt className="hairline opacity-55">Tickets</dt>
                    <dd className="font-serif italic text-lg mt-1">
                      <a
                        href={TICKETS_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="link-quiet"
                        data-testid="pr-tickets-link"
                      >
                        chapter2.nickfraserartist.com →
                      </a>
                    </dd>
                  </div>
                </dl>
              </div>
            </Reveal>

            {/* Body copy */}
            <div className="md:col-span-8">
              <Reveal slow>
                <p className="font-serif italic text-2xl md:text-3xl leading-[1.3]">
                  On <strong className="not-italic font-serif italic">31 July 2026</strong>,
                  Nick Fraser: Chapter II opens for one day only at{" "}
                  <a
                    href={VENUE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="link-quiet"
                    data-testid="pr-venue-link"
                  >
                    {VENUE_NAME}
                  </a>
                  &nbsp;&mdash; presenting the next stage in
                  the remarkable rediscovery of one of East London&apos;s most
                  enigmatic artistic voices.
                </p>
              </Reveal>

              <Reveal slow>
                <p className="font-serif italic text-lg md:text-xl mt-10 opacity-85 leading-[1.55]">
                  Following the success of Chapter I and the growing
                  recognition surrounding Fraser&apos;s work, Chapter II expands
                  the story beyond the paintings, offering audiences a deeper
                  and more intimate insight into the life behind the canvas.
                </p>
              </Reveal>

              <Reveal slow>
                <p className="font-serif italic text-lg md:text-xl mt-6 opacity-85 leading-[1.55]">
                  Born in 1946 and known privately for decades as a fiercely
                  independent and deeply contemplative artist, Fraser created
                  a powerful body of work exploring masculinity, spirituality,
                  identity, memory and human existence. Working largely
                  outside the traditional art world, he developed an
                  uncompromising visual language that remained hidden from
                  public view for much of his life.
                </p>
              </Reveal>

              <Reveal slow>
                <p className="font-serif italic text-lg md:text-xl mt-6 opacity-85 leading-[1.55]">
                  This new exhibition brings together a curated selection of
                  original works alongside previously unseen archival
                  materials, rare photographs and intimate film footage
                  captured over many years by Fraser&apos;s lifelong friend and
                  custodian, Mark O&apos;Shaughnessy. For the first time,
                  audiences will be invited deeper into Fraser&apos;s world
                  through moving image, personal history and the
                  environments that shaped both the artist and his practice.
                </p>
              </Reveal>

              <Reveal slow>
                <p className="font-serif italic text-lg md:text-xl mt-6 opacity-85 leading-[1.55]">
                  The exhibition carefully explores the context behind the
                  work. It reflects a life lived within the traditions of
                  the East End while carrying deeper questions surrounding
                  identity, self-expression and emotional vulnerability.
                  Rather than seeking to define Fraser, Chapter II creates
                  space to reflect upon the complexity of the man behind
                  the paintings.
                </p>
              </Reveal>

              {/* Mark quote */}
              <Reveal slow>
                <figure
                  className="mt-16 pl-6 md:pl-8 border-l-2 border-[color:var(--ink)]"
                  data-testid="pr-quote-mark"
                >
                  <blockquote className="font-serif italic text-2xl md:text-3xl leading-[1.3]">
                    “It was an honour to call Nick a friend, to stand by him
                    through his life, and later to be trusted with his work.
                    To now see his story being shared, and brought back to
                    the East End where we spent so much time together, means
                    a great deal to me.”
                  </blockquote>
                  <figcaption className="hairline mt-6 opacity-60">
                    Mark O&apos;Shaughnessy  ·  Friend &amp; Custodian
                  </figcaption>
                </figure>
              </Reveal>

              <Reveal slow>
                <p className="font-serif italic text-lg md:text-xl mt-16 opacity-85 leading-[1.55]">
                  Over recent years, Fraser&apos;s archive and legacy have
                  continued through the work of curator Marcus
                  O&apos;Shaughnessy, whose ongoing research, preservation and
                  public presentation of the collection has brought renewed
                  attention to the artist&apos;s extraordinary life and work.
                </p>
              </Reveal>

              {/* Marcus quote */}
              <Reveal slow>
                <figure
                  className="mt-12 pl-6 md:pl-8 border-l-2 border-[color:var(--ink)]"
                  data-testid="pr-quote-marcus"
                >
                  <blockquote className="font-serif italic text-2xl md:text-3xl leading-[1.3]">
                    “Over the past few years, Nick Fraser&apos;s journey has
                    profoundly shaped my own. What began as an act of care
                    has become something much deeper — a responsibility and
                    a privilege. To bring his work back to the East End, the
                    place that formed him, feels especially meaningful.”
                  </blockquote>
                  <figcaption className="hairline mt-6 opacity-60">
                    Marcus O&apos;Shaughnessy  ·  Curator
                  </figcaption>
                </figure>
              </Reveal>

              <Reveal slow>
                <p className="font-serif italic text-lg md:text-xl mt-16 opacity-85 leading-[1.55]">
                  Set within Shoreditch Arts Club — an East End members club
                  and cultural events space for artists and curators —
                  Chapter II will unfold as an immersive one-day experience
                  combining original artworks, archival projection, rare
                  film footage and a live musical performance from{" "}
                  <em>BoThai</em>.
                </p>
              </Reveal>

              <Reveal slow>
                <p className="font-serif italic text-lg md:text-xl mt-6 opacity-90 leading-[1.55]">
                  More than an exhibition, Chapter II is an act of cultural
                  preservation and rediscovery — reconnecting an overlooked
                  artist with the city, community and streets that shaped
                  his life.
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ── Trailer ─────────────────────────────────────────────── */}
        <EventTrailer
          testid="pr-trailer"
          variant="dark"
          eyebrow="The Trailer"
          title="The official Chapter 2 advert."
          body="A short cinematic introduction to the exhibition — the work, the venue and the evening. Embed-ready for editorial use."
        />

        {/* ── Press contact strip ─────────────────────────────────── */}
        <section
          className="dark-section py-28 md:py-40"
          data-testid="pr-contact"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
            <Reveal className="md:col-span-5">
              <p className="hairline opacity-60 mb-6">Press enquiries</p>
              <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.1]">
                For interviews, images and accreditation.
              </h2>
            </Reveal>
            <Reveal className="md:col-span-7" slow>
              <dl className="space-y-6">
                <div>
                  <dt className="hairline opacity-55">Email</dt>
                  <dd className="font-serif italic text-xl md:text-2xl mt-1">
                    <a
                      href="mailto:info@nickfraserartist.com"
                      className="link-quiet"
                      data-testid="pr-email"
                    >
                      info@nickfraserartist.com
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="hairline opacity-55">Web</dt>
                  <dd className="font-serif italic text-xl md:text-2xl mt-1">
                    <a
                      href="https://www.nickfraserartist.com"
                      target="_blank"
                      rel="noreferrer"
                      className="link-quiet"
                    >
                      www.nickfraserartist.com
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="hairline opacity-55">Instagram</dt>
                  <dd className="font-serif italic text-xl md:text-2xl mt-1">
                    <a
                      href="https://instagram.com/nickfraserart"
                      target="_blank"
                      rel="noreferrer"
                      className="link-quiet"
                    >
                      @nickfraserart
                    </a>
                  </dd>
                </div>
              </dl>
              <p className="font-serif italic text-base md:text-lg opacity-65 mt-12 max-w-md leading-relaxed">
                Press kits, high-resolution images, exhibition photography,
                archive stills and interview opportunities with Marcus
                O&apos;Shaughnessy are available on request.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
                <a
                  href={PDF_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="pr-download-pdf-bottom"
                  className="hairline border border-[color:var(--bone)] px-6 py-3 hover:bg-[color:var(--bone)] hover:text-[color:var(--ink)] transition-colors duration-500"
                >
                  Download PDF
                </a>
                <a
                  href={MEDIA_FOLDER_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="pr-media-folder-bottom"
                  className="link-quiet font-serif italic text-lg"
                >
                  Press image library →
                </a>
                <Link
                  to="/press"
                  className="link-quiet font-serif italic text-lg"
                  data-testid="pr-back-to-press"
                >
                  Back to press →
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </Layout>
  );
}
